import { useState } from 'react'
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ETICHETTA_SCADENZA, PREDEFINITI_SCADENZA, TIPI_SCADENZA, etichettaPeriodicita, titoloScadenza,
  type Ricorrenza, type StatoScadenza, type TipoScadenza,
} from '@praticheenergia/domain'
import { caricaElenco } from '../impianti/impianti.api'
import {
  SchemaScadenza, completaScadenza, elencaScadenze, eliminaScadenza, salvaScadenza,
  type DatiScadenza, type Scadenza,
} from './scadenze.api'

const NESSUNA: Scadenza[] = []

const ETICHETTA_STATO: Record<StatoScadenza, string> = {
  scaduta: 'Scaduta',
  in_avvicinamento: 'In scadenza',
  futura: 'Futura',
  completata: 'Completata',
}

const COLORE_STATO: Record<StatoScadenza, 'error' | 'warning' | 'default' | 'success'> = {
  scaduta: 'error',
  in_avvicinamento: 'warning',
  futura: 'default',
  completata: 'success',
}

const oggi = () => new Date().toISOString().slice(0, 10)

// Scegliere l'adempimento propone preavviso e ricorrenza abituali (PREDEFINITI_SCADENZA).
const conPredefiniti = (tipo: TipoScadenza) => {
  const p = PREDEFINITI_SCADENZA[tipo]
  return { tipo, giorniPreavviso: p.giorniPreavviso, periodicitaMesi: p.periodicitaMesi, ricorrenza: p.ricorrenza }
}

const VUOTA = (impiantoId: number): DatiScadenza => ({
  impiantoId,
  titolo: '',
  scadenza: '',
  note: '',
  importo: '',
  ...conPredefiniti('taratura_contatori'),
})

const PERIODICITA = [null, 6, 12, 24, 36, 60] as const

// Da `2026-09-30` a `30/09/2026` senza passare da Date: niente slittamenti di fuso.
const dataItaliana = (iso: string) => iso.split('-').reverse().join('/')

export function Scadenzario() {
  const queryClient = useQueryClient()
  const [completate, setCompletate] = useState(false)
  const [modulo, setModulo] = useState<{ id: number | null; dati: DatiScadenza } | null>(null)
  const [errore, setErrore] = useState<string | null>(null)

  const { data: scadenze = NESSUNA, error } = useQuery({ queryKey: ['scadenze'], queryFn: elencaScadenze })
  // Gli impianti servono solo a scegliere a chi appartiene la scadenza.
  const { data: impianti = [] } = useQuery({ queryKey: ['impianti', null, null], queryFn: () => caricaElenco(null) })

  // Le completate sono archivio: si guardano quando si vuole, non ogni giorno.
  const visibili = scadenze.filter((s) => (s.stato === 'completata') === completate)

  async function conEsito(azione: () => Promise<void>) {
    setErrore(null)
    try {
      await azione()
      await queryClient.invalidateQueries({ queryKey: ['scadenze'] })
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'errore imprevisto')
    }
  }

  async function suSalva() {
    if (modulo === null) return
    const esito = SchemaScadenza.safeParse(modulo.dati)
    if (!esito.success) {
      setErrore(esito.error.issues[0]!.message)
      return
    }
    setErrore(null)
    try {
      await salvaScadenza(modulo.id, modulo.dati)
      setModulo(null)
      await queryClient.invalidateQueries({ queryKey: ['scadenze'] })
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'errore imprevisto')
    }
  }

  const imposta = <K extends keyof DatiScadenza>(campo: K, valore: DatiScadenza[K]) =>
    setModulo((m) => (m === null ? m : { ...m, dati: { ...m.dati, [campo]: valore } }))

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Scadenzario
      </Typography>
      {error && <Alert severity="error" role="alert" sx={{ mb: 2 }}>{error instanceof Error ? error.message : 'Errore nel caricamento'}</Alert>}
      {errore !== null && modulo === null && <Alert severity="error" role="alert" sx={{ mb: 2 }}>{errore}</Alert>}

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button variant={completate ? 'outlined' : 'contained'} onClick={() => setCompletate(false)}>
          Da fare
        </Button>
        <Button variant={completate ? 'contained' : 'outlined'} onClick={() => setCompletate(true)}>
          Completate
        </Button>
      </Stack>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Adempimento</TableCell>
            <TableCell>Impianto</TableCell>
            <TableCell>Scadenza</TableCell>
            <TableCell>Stato</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {visibili.map((s) => {
            const nome = titoloScadenza(s)
            return (
              <TableRow key={s.id}>
                <TableCell>
                  <Typography variant="body2">{nome}</Typography>
                  {s.note !== '' && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{s.note}</Typography>
                  )}
                  {s.avvisataIl !== null && (
                    <Typography variant="caption" color="text.secondary">
                      Avviso al cliente inviato il {dataItaliana(s.avvisataIl)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{s.impianto}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {dataItaliana(s.scadenza)}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {etichettaPeriodicita(s.periodicitaMesi)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip size="small" label={ETICHETTA_STATO[s.stato]} color={COLORE_STATO[s.stato]} />
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    aria-label={`${s.stato === 'completata' ? 'Riapri' : 'Completa'} ${nome}`}
                    onClick={() => void conEsito(() => completaScadenza(s.id, s.stato === 'completata' ? null : oggi()))}
                  >
                    {s.stato === 'completata' ? 'Riapri' : 'Completa'}
                  </Button>
                  <Button
                    size="small"
                    aria-label={`Modifica ${nome}`}
                    onClick={() => {
                      setErrore(null)
                      setModulo({
                        id: s.id,
                        dati: {
                          impiantoId: s.impiantoId,
                          tipo: s.tipo,
                          titolo: s.titolo ?? '',
                          scadenza: s.scadenza,
                          giorniPreavviso: s.giorniPreavviso,
                          note: s.note,
                          periodicitaMesi: s.periodicitaMesi,
                          ricorrenza: s.ricorrenza,
                          importo: s.importo?.replace('.', ',') ?? '',
                        },
                      })
                    }}
                  >
                    Modifica
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    aria-label={`Elimina ${nome}`}
                    onClick={() => {
                      if (!window.confirm(`Eliminare la scadenza «${nome}»?`)) return
                      void conEsito(() => eliminaScadenza(s.id))
                    }}
                  >
                    Elimina
                  </Button>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      {visibili.length === 0 && <p>{completate ? 'Nessuna scadenza completata' : 'Nessuna scadenza da seguire'}</p>}

      <Button
        variant="outlined"
        sx={{ mt: 2 }}
        disabled={impianti.length === 0}
        onClick={() => {
          setErrore(null)
          setModulo({ id: null, dati: VUOTA(impianti[0]!.id) })
        }}
      >
        Aggiungi scadenza
      </Button>

      <Dialog open={modulo !== null} onClose={() => setModulo(null)} fullWidth maxWidth="sm">
        <DialogTitle>{modulo?.id === null ? 'Nuova scadenza' : 'Modifica scadenza'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {errore !== null && <Alert severity="error" role="alert">{errore}</Alert>}
            <TextField
              select
              size="small"
              label="Impianto"
              slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
              value={modulo?.dati.impiantoId ?? ''}
              onChange={(e) => imposta('impiantoId', Number(e.target.value))}
            >
              {impianti.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.ragioneSociale} ({i.codiceDichiarazione})
                </option>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Adempimento"
              slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
              value={modulo?.dati.tipo ?? 'taratura_contatori'}
              onChange={(e) => {
                const tipo = e.target.value as TipoScadenza
                // Su una scadenza esistente si cambia solo il tipo: i valori scelti restano.
                setModulo((m) => m && { ...m, dati: { ...m.dati, ...(m.id === null ? conPredefiniti(tipo) : { tipo }) } })
              }}
            >
              {TIPI_SCADENZA.map((t) => (
                <option key={t} value={t}>
                  {ETICHETTA_SCADENZA[t]}
                </option>
              ))}
            </TextField>
            {modulo?.dati.tipo === 'personalizzata' && (
              <TextField
                size="small"
                label="Titolo"
                value={modulo.dati.titolo}
                onChange={(e) => imposta('titolo', e.target.value)}
              />
            )}
            <TextField
              size="small"
              type="date"
              label="Data"
              slotProps={{ inputLabel: { shrink: true } }}
              value={modulo?.dati.scadenza ?? ''}
              onChange={(e) => imposta('scadenza', e.target.value)}
            />
            <TextField
              size="small"
              type="number"
              label="Giorni di preavviso"
              value={modulo?.dati.giorniPreavviso ?? 30}
              onChange={(e) => imposta('giorniPreavviso', e.target.value === '' ? 0 : Number(e.target.value))}
            />
            <TextField
              select
              size="small"
              label="Si ripete"
              slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
              value={modulo?.dati.periodicitaMesi ?? ''}
              onChange={(e) => {
                const mesi = e.target.value === '' ? null : Number(e.target.value)
                setModulo((m) => m && {
                  ...m,
                  dati: { ...m.dati, periodicitaMesi: mesi, ricorrenza: mesi === null ? null : (m.dati.ricorrenza ?? 'da_scadenza') },
                })
              }}
            >
              {PERIODICITA.map((p) => (
                <option key={p ?? 'mai'} value={p ?? ''}>
                  {etichettaPeriodicita(p)}
                </option>
              ))}
            </TextField>
            {modulo?.dati.periodicitaMesi != null && (
              <TextField
                select
                size="small"
                label="La prossima si conta"
                helperText="Fatto l'adempimento, la scadenza successiva si crea da sola"
                slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
                value={modulo.dati.ricorrenza ?? 'da_scadenza'}
                onChange={(e) => imposta('ricorrenza', e.target.value as Ricorrenza)}
              >
                <option value="da_scadenza">dalla data di scadenza (date fisse, es. 16/12)</option>
                <option value="da_rinnovo">dal giorno in cui è stato fatto (verifiche, tarature)</option>
              </TextField>
            )}
            {modulo?.dati.tipo === 'adeguamento_cci' && (
              <TextField
                size="small"
                label="Importo (€)"
                inputMode="decimal"
                value={modulo.dati.importo ?? ''}
                onChange={(e) => imposta('importo', e.target.value)}
              />
            )}
            <TextField
              size="small"
              label="Note"
              multiline
              value={modulo?.dati.note ?? ''}
              onChange={(e) => imposta('note', e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModulo(null)}>Annulla</Button>
          <Button variant="contained" onClick={() => void suSalva()}>
            Salva
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
